// src/pages/SetPassword.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  LinearProgress,
  Stack,
  Avatar,
  Divider,
  Chip,
  Skeleton,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ReportGmailerrorredRoundedIcon from "@mui/icons-material/ReportGmailerrorredRounded";
import { useTheme } from "@mui/material/styles";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchData } from "../../api/client";
import logo from "../../assets/logo.png";
import logowhite from "../../assets/logowhite.png";
import { PASSWORD_POLICY_CHECKS } from "../../utils/passwordPolicy";

const RULE_LABELS_PT = {
  len: "Mínimo 8 caracteres",
  upper: "Letra maiúscula",
  num: "Número",
  sym: "Símbolo",
};

const rules = PASSWORD_POLICY_CHECKS.map((c) => ({ ...c, label: RULE_LABELS_PT[c.id] }));

const COMPANY_RE = /^[a-zA-Z0-9._-]{2,32}$/;
const toSlug = (v) => String(v || "").trim().toLowerCase();
const strengthOf = (s) => rules.reduce((acc, r) => acc + (r.test(s) ? 1 : 0), 0);

export default function SetPassword() {
  const theme = useTheme();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const cParam = params.get("c") || "";
  const navigate = useNavigate();

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  // Estados de validação do token
  const [tokenChecking, setTokenChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenMsg, setTokenMsg] = useState("");

  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  // Captura/normaliza workspace do param ou localStorage
  const workspace = useMemo(() => {
    const q = toSlug(cParam);
    if (q && COMPANY_RE.test(q)) return q;
    const stored = toSlug(localStorage.getItem("company") || "");
    return COMPANY_RE.test(stored) ? stored : "";
  }, [cParam]);

  // Persistir workspace válido em localStorage
  useEffect(() => {
    if (workspace && COMPANY_RE.test(workspace)) {
      if (localStorage.getItem("company") !== workspace) {
        localStorage.setItem("company", workspace);
      }
    }
  }, [workspace]);

  // Validação imediata do token no carregamento (sem redirect)
  useEffect(() => {
    const run = async () => {
      if (!token) {
        setTokenChecking(false);
        setTokenValid(false);
        setTokenMsg("Link inválido ou expirado. Solicite um novo convite ou redefinição.");
        return;
      }
      try {
        setTokenChecking(true);
        setTokenMsg("");
        await fetchData(`/users/validate-password-token`, "POST", { token });
        setTokenValid(true);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          "Este link é inválido ou expirou. Peça um novo convite ou nova redefinição de senha.";
        setTokenValid(false);
        setTokenMsg(msg);
      } finally {
        setTokenChecking(false);
      }
    };
    run();
  }, [token]);

  const score = useMemo(() => strengthOf(pwd), [pwd]);
  const allRulesOk = score === rules.length;
  const confirmOk = pwd && confirm && pwd === confirm;
  const canSubmit = !busy && tokenValid && allRulesOk && confirmOk;
  const progress = useMemo(() => (score / rules.length) * 100, [score]);

  const submit = async (e) => {
    e.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);

    try {
      await fetchData(`/users/complete-password`, "POST", {
        token,
        new_password: pwd,
      });
      // Sucesso: NÃO redireciona automaticamente
      setOk(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Não foi possível definir a senha. Tente novamente ou solicite um novo link.";
      // Erro: permanece na tela e orienta
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const goLoginWithWorkspace = () => {
    const qs = workspace ? `?c=${encodeURIComponent(workspace)}` : "";
    navigate("/login" + qs);
  };

  // ===================== RENDER =====================

  // Sucesso: mensagem + botão para ir ao login (sem auto-redirect)
  if (ok) {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh" sx={{
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #121212, #1e1e1e)"
          : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
      }}>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <img src={theme.palette.mode === "dark" ? logowhite : logo} alt="Xpro" style={{ height: 60 }} loading="lazy" />
        </Box>

        <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 2 }}>
          <Paper elevation={6} sx={{ width: "100%", maxWidth: 480, p: 4, borderRadius: 3 }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Avatar sx={{ bgcolor: "success.main", width: 56, height: 56 }}>
                <CheckCircleRoundedIcon />
              </Avatar>
              <Typography variant="h5" fontWeight={700}>Senha definida com sucesso</Typography>
              <Typography color="text.secondary">Agora você já pode acessar a plataforma com seu e-mail e a nova senha.</Typography>
              {workspace && (<Chip label={`Workspace: ${workspace}`} variant="outlined" size="small" />)}
              <Button variant="contained" onClick={goLoginWithWorkspace} startIcon={<LockResetRoundedIcon />} sx={{ height: 44, px: 3 }}>
                Ir para o login
              </Button>
            </Stack>
          </Paper>
        </Box>

        <Box py={2} textAlign="center">
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            © {new Date().getFullYear()} Xpro. Todos os direitos reservados.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Token em checagem: placeholder leve (sem mostrar o form)
  if (tokenChecking) {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh" sx={{
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #121212, #1e1e1e)"
          : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
      }}>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <img src={theme.palette.mode === "dark" ? logowhite : logo} alt="Xpro" style={{ height: 60 }} loading="lazy" />
        </Box>
        <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 2 }}>
          <Paper elevation={6} sx={{ width: "100%", maxWidth: 520, p: 4, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </Box>
              </Stack>
              <Skeleton height={72} />
              <Skeleton height={48} />
            </Stack>
          </Paper>
        </Box>
        <Box py={2} textAlign="center">
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            © {new Date().getFullYear()} Xpro. Todos os direitos reservados.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Token inválido/expirado: NÃO mostra o formulário, NÃO redireciona
  if (!tokenValid) {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh" sx={{
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #121212, #1e1e1e)"
          : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
      }}>
        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
          <img src={theme.palette.mode === "dark" ? logowhite : logo} alt="Xpro" style={{ height: 60 }} loading="lazy" />
        </Box>

        <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 2 }}>
          <Paper elevation={6} sx={{ width: "100%", maxWidth: 520, p: 4, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: "error.main", width: 40, height: 40 }}>
                  <ReportGmailerrorredRoundedIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>Link inválido ou expirado</Typography>
                  {workspace && (<Typography variant="body2" color="text.secondary">Workspace: {workspace}</Typography>)}
                </Box>
              </Stack>

              <Alert severity="error">{tokenMsg}</Alert>

              <Typography variant="body2" color="text.secondary">
                O que você pode fazer agora:
                <br />• Voltar ao login e solicitar um <strong>novo link</strong> de convite/redefinição.
                <br />• Verificar se você usou o link mais recente recebido por e-mail.
              </Typography>

              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button variant="contained" onClick={goLoginWithWorkspace} startIcon={<ArrowBackRoundedIcon />}>
                  Voltar ao login
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <Box py={2} textAlign="center">
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            © {new Date().getFullYear()} Xpro. Todos os direitos reservados.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Formulário principal (apenas com token válido)
  return (
    <Box display="flex" flexDirection="column" minHeight="100vh" sx={{
      background: theme.palette.mode === "dark"
        ? "linear-gradient(135deg, #121212, #1e1e1e)"
        : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
    }}>
      {/* Header com logo */}
      <Box display="flex" alignItems="center" justifyContent="center" py={4}>
        <img src={theme.palette.mode === "dark" ? logowhite : logo} alt="Xpro" style={{ height: 60 }} loading="lazy" />
      </Box>

      {/* Conteúdo */}
      <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 2 }}>
        <Paper
          elevation={6}
          component="form"
          onSubmit={submit}
          sx={{ width: "100%", maxWidth: 520, p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: theme.palette.background.paper }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                <LockOutlinedIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>Definir nova senha</Typography>
                <Typography variant="body2" color="text.secondary">Crie uma senha forte para proteger sua conta.</Typography>
              </Box>
            </Stack>

            {workspace && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`Workspace: ${workspace}`} size="small" variant="outlined" />
              </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Nova senha"
              type={showPwd ? "text" : "password"}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoFocus
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd((v) => !v)} edge="end" aria-label="alternar exibição da senha">
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 8, borderRadius: 999, bgcolor: theme.palette.mode === "light" ? "#eee" : "rgba(255,255,255,.12)", mb: 1 }}
              />
              <Stack spacing={0.5}>
                {rules.map((r) => {
                  const ok = r.test(pwd);
                  return (
                    <Typography key={r.id} variant="caption" color={ok ? "success.main" : "text.secondary"}>
                      {ok ? "✔" : "•"} {r.label}
                    </Typography>
                  );
                })}
              </Stack>
            </Box>

            <TextField
              label="Confirmar senha"
              type={showConfirm ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={Boolean(confirm) && confirm !== pwd}
              helperText={Boolean(confirm) && confirm !== pwd ? "As senhas não coincidem" : " "}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" aria-label="alternar exibição da senha">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Button variant="text" onClick={goLoginWithWorkspace} startIcon={<ArrowBackRoundedIcon />} disabled={busy}>
                Voltar
              </Button>
              <Button type="submit" variant="contained" disabled={!canSubmit} startIcon={<LockResetRoundedIcon />} sx={{ height: 44, px: 3, fontWeight: 700 }}>
                {busy ? "Salvando…" : "Definir senha"}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {/* Footer */}
      <Box py={2} textAlign="center">
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          © {new Date().getFullYear()} Xpro. Todos os direitos reservados.
        </Typography>
      </Box>
    </Box>
  );
}
